/* eslint-disable @typescript-eslint/naming-convention */
import { useQuery } from '@apollo/client';

import GetSkillAreasWithUserDataQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetSkillAreasWithUserData.graphql';
import type {
  GetSkillAreasWithUserDataQuery as QueryResult,
  GetSkillAreasWithUserDataQueryVariables as QueryVariables,
  SkillAreaWithUserDataFragment,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAreasWithUserData';

type Props = {
  programId: string;
  skip?: boolean;
};

export type SkillAreaWithUserData = {
  assessmentItemId: string | null;
  skillArea: {
    id: string;
    name: string;
    course: {
      id: string;
      slug: string;
    };
    skillExpressions: Array<{
      id: string;
      name: string;
      description: string | null | undefined;
    }>;
  };
};

const useGetSkillAreasWithUserData = ({ programId, skip }: Props) => {
  const { data, loading, error } = useQuery<QueryResult, QueryVariables>(GetSkillAreasWithUserDataQuery, {
    variables: {
      programId,
    },
    skip: skip || !programId,
    context: { clientName: 'gatewayGql' },
  });

  const skillAreasWithUserData: SkillAreaWithUserData[] =
    data?.VerifiedSkills?.getAllSkillPathwaysByProgramId
      ?.flatMap((pathway) => pathway?.skillAreasWithUserData || [])
      ?.filter((item): item is SkillAreaWithUserDataFragment => item !== null && item !== undefined)
      ?.map((item) => ({
        assessmentItemId: item.assessmentItemId,
        skillArea: {
          id: item.skillArea.id,
          name: item.skillArea.name,
          course: {
            id: item.skillArea.course.id,
            slug: item.skillArea.course.slug,
          },
          skillExpressions: item.skillArea.skillExpressions.map((expression) => ({
            id: expression.id,
            name: expression.name,
            description: expression.description,
          })),
        },
      })) || [];

  return {
    skillAreasWithUserData,
    loading,
    error,
  };
};

export default useGetSkillAreasWithUserData;
